import { copyText } from '../copied'
import { ApiError, changeNow, SIGNED_OUT_MESSAGE } from '../api'
import { clear, el } from '../ui'
import '../rounds.css'
import '../partners.css'

// Partners (admin only; Liam, 19 Oct 2026: "fully joined", then "Yes build it"; design
// https://claude.ai/artifact/NtbeDHkkztLoFtTQ3MNZh5 after Sonnet's and GPT-6.1's attack and Astra's look; plan
// ~/Desktop/Sites/roundly-mb-wt/partners-in-app-20261019/PLAN.md). The partner admin lives here now, in the Rounds
// page's look: the partners and their money along the top, Needs you (applications, payouts, referral checks, quiet
// test partners, hoodies) with To do and Done, every partner on one line with what happened, and a partner opened beside
// the list (on a phone, full screen with Back). The partner service works everything out and does every change
// (functions/lib/partners-admin.ts); each button asks first, needs a connection, is never kept to send later and has no
// Undo, because an email or a payout can't be taken back. Pressing again after no answer sends the same request, so
// nothing is sent or recorded twice.

interface Payable { pence: number; refs: string[]; deductions: string[] }
interface Partner {
  id: string
  name: string
  email: string
  channel: string
  code: string
  rate: 40 | 30
  place: number
  joinedAt: number
  stoppedAt: number | null
  welcomeSent: boolean
  // Roundly still retries the welcome (the first two days); after that it has stopped trying.
  welcomeStillTrying?: boolean
  bank: boolean
  businesses: { total: number; paying: number; free: number; stopped: number }
  readyPence: number
  heldPence: number
  checkPence: number
  deductPence: number
  // Everything earned on businesses that count and not yet paid, less refunds to come off: what a stop still pays.
  owedPence?: number
  paidPence: number
  couldPence: number
  lastSignupAt: number | null
  firstPaidAt: number | null
  checkAfter: number | null
  hoodieDue?: boolean
  hoodieSentAt?: number | null
  payable: Payable | null
}
interface Application { id: string; name: string; email: string; channel: string; audience: string; createdAt: number }
interface DeclineUnsent { id: string; name: string; email: string; declinedAt: number; stillTrying: boolean }
interface Check { businessRef: string; partnerId: string; partnerName: string; signedUpAt: number; payments: number }
interface Quiet { partnerId: string; name: string; joinedAt: number }
interface DoneRow { kind: string; outcome: string; detail: { name?: string; pence?: number; businessRef?: string; owedPence?: number }; partnerId: string | null; at: number }
interface FeedRow { at: number; kind: string; text: string }
export interface PartnersPage {
  now: number
  payoutDay: string
  partners: Partner[]
  applications: Application[]
  declinesUnsent: DeclineUnsent[]
  checks: Check[]
  quiet: Quiet[]
  done: DoneRow[]
  feed: FeedRow[]
  places: { used: number; rateNow: 40 | 30 }
  month: { revenuePence: number; commissionPence: number; allStayPence: number; allStayCount: number }
  payouts: { sentPence: number; duePence: number }
  rounds: Record<string, { roundId: number; name: string; givenAt: string | null; freeUntil: string | null }>
  refs: Record<string, { roundId: number; name: string; status: string; code: string }>
  billing: { on: boolean; connected: boolean; waiting: number; faults: number; oldestWaiting: string | null; lastSent: string | null }
}
interface ActionAnswer { ok: boolean; outcome: string; note: string; replayed?: boolean }

type Read = (path: string) => Promise<unknown>
type Asking = { id: string; what: string }

const FOUNDING = 10
const DAY = 86400
const SIGNUP_PAGE = 'https://roundly.work/partners'
// The width up to which an open partner fills the screen with Back (rounds.css, the same as the Rounds page).
const FULL_SCREEN = '(max-width: 1100px)'

let data: PartnersPage | null = null
let failed: string | null = null
let loading = false
let tab: 'todo' | 'done' = 'todo'
let asking: Asking | null = null
let stopAsking = false
let open: string | null = null
// What the last button did, in the partner service's words; shown where it was pressed (the panel or Needs you).
let note: { text: string; warn: boolean; signIn?: boolean; panel?: boolean } | null = null
let busy: string | null = null
let bankError: string | null = null
let feedAll = false
let sequence = 0
let scrollBeforeOpen = 0
// The request key of a press that got no answer, kept with the request itself: pressing again sends the same request
// under the same key (never twice), and a request that changed since (a new amount) gets a key of its own (Sol).
const keys = new Map<string, string>()
// The button a question was opened from, so Not now or Escape puts the focus back on it.
let opener: { id: string; what: string } | null = null
let read: Read = async () => { throw new Error('not mounted') }
let active: () => boolean = () => false

// ---- words -------------------------------------------------------------------------------------------------------

const POUNDS = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' })
const money = (pence: number): string => POUNDS.format((pence || 0) / 100)
const first = (name: string): string => (name.trim().split(/\s+/)[0] || name).slice(0, 40)
const plural = (n: number, one: string, many = `${one}s`): string => `${n} ${n === 1 ? one : many}`
const LONDON = 'Europe/London'
const dayShort = (sec: number): string => new Intl.DateTimeFormat('en-GB', { timeZone: LONDON, weekday: 'short', day: 'numeric', month: 'short' }).format(new Date(sec * 1000))
const dateFull = (sec: number): string => new Intl.DateTimeFormat('en-GB', { timeZone: LONDON, day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(sec * 1000))
const dateDay = (sec: number): string => new Intl.DateTimeFormat('en-GB', { timeZone: LONDON, day: 'numeric', month: 'short' }).format(new Date(sec * 1000))
const monthYear = (sec: number): string => new Intl.DateTimeFormat('en-GB', { timeZone: LONDON, month: 'long', year: 'numeric' }).format(new Date(sec * 1000))
const monthName = (sec: number): string => new Intl.DateTimeFormat('en-GB', { timeZone: LONDON, month: 'long' }).format(new Date(sec * 1000))
function stamp(sec: number): string {
  const time = new Intl.DateTimeFormat('en-GB', { timeZone: LONDON, hour: 'numeric', minute: '2-digit', hour12: true }).format(new Date(sec * 1000)).replace(/\s/g, '').toLowerCase()
  return `${dayShort(sec)}, ${time}`
}
// The 5th, from its YYYY-MM-DD: "Wednesday 5 May" or "Wed 5 May".
const isoSec = (iso: string): number => Math.floor(Date.parse(`${iso}T12:00:00Z`) / 1000)
const payDayLong = (iso: string): string => new Intl.DateTimeFormat('en-GB', { timeZone: LONDON, weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(isoSec(iso) * 1000))
const isoDate = (iso: string): string => dateFull(isoSec(iso))
function monthsWords(seconds: number): string {
  const months = Math.floor(seconds / (30.44 * DAY))
  return months >= 2 ? `${months} months` : plural(Math.max(1, Math.floor(seconds / DAY)), 'day')
}
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?'
}
const founding = (p: Partner): boolean => p.rate === 40 && p.place <= FOUNDING
const owedOf = (p: Partner): number => p.owedPence ?? Math.max(0, p.readyPence + p.heldPence)
function hostOf(link: string): string {
  try {
    const u = new URL(link)
    return `${u.hostname.replace(/^www\./i, '')}${u.pathname === '/' ? '' : u.pathname}`
  } catch {
    return link
  }
}
const safeLink = (link: string): string | null => {
  try {
    const u = new URL(link)
    return ['https:', 'http:'].includes(u.protocol) && !u.username && !u.password ? u.href : null
  } catch {
    return null
  }
}
// billing.oldestWaiting is SQLite's UTC "YYYY-MM-DD HH:MM:SS".
const sqlSec = (v: string): number => Math.floor(new Date(`${v.replace(' ', 'T')}${v.endsWith('Z') ? '' : 'Z'}`).getTime() / 1000)

// ---- loading and buttons -----------------------------------------------------------------------------------------

async function load(root: HTMLElement): Promise<void> {
  const mine = ++sequence
  loading = true
  try {
    const page = await read('/api/admin/partners') as PartnersPage
    if (!active() || mine !== sequence) return
    data = page
    failed = null
  } catch (err) {
    if (!active() || mine !== sequence) return
    // Nothing old is shown as if it were today's: money hidden rather than shown as £0.00.
    data = null
    failed = (err as Error).message === SIGNED_OUT_MESSAGE ? 'signed-out' : 'unreachable'
  } finally {
    if (mine === sequence) loading = false
  }
  if (open && !data?.partners.some((p) => p.id === open)) open = null
  render(root)
}

// One of Liam's buttons, after he said yes. A refusal (already done on another screen, changed since the page loaded,
// under £20) is shown in the partner service's words; no answer keeps the question open to press again, same request.
async function act(root: HTMLElement, id: string, body: Record<string, unknown>): Promise<void> {
  if (busy) return
  const sent = `${id} ${JSON.stringify(body)}`
  const key = keys.get(sent) ?? crypto.randomUUID()
  keys.set(sent, key)
  busy = id
  note = null
  const panel = id.startsWith('stop:')
  render(root)
  try {
    const answer = await changeNow<ActionAnswer>('POST', '/api/admin/partners/action', body, key)
    keys.delete(sent)
    note = { text: answer.note, warn: /-unsent$/.test(answer.outcome), panel }
    asking = null
    stopAsking = false
  } catch (err) {
    const e = err as ApiError
    if (e.message === SIGNED_OUT_MESSAGE || e.status === 401) {
      note = { text: "You've been signed out. Sign in again to accept, decline or record a payout.", warn: true, signIn: true, panel }
    } else if (e.code === 'busy') {
      // The first press is still being done: the question stays, and pressing again sends the same request.
      note = { text: e.message, warn: true, panel }
    } else if ([400, 404, 409, 422].includes(e.status)) {
      keys.delete(sent)
      note = { text: e.message, warn: true, panel }
      asking = null
      stopAsking = false
    } else {
      note = { text: 'No answer from the partner records, so this may not have been done. Press it again: if it was done, nothing is sent or recorded twice.', warn: true, panel }
    }
  } finally {
    busy = null
  }
  if (!active()) return
  render(root)
  await load(root)
  root.querySelector<HTMLElement>('.partners-note')?.focus()
}

// ---- Needs you ---------------------------------------------------------------------------------------------------

interface Need {
  id: string
  kind: string
  title: string
  detail: string
  due?: { text: string; tone: 2 | 3 | 1 }
  buttons: { label: string; what: string; primary?: boolean; href?: string; open?: string }[]
  ask: Record<string, string>
  yes: Record<string, string>
  body: Record<string, Record<string, unknown>>
  said?: Application
}

function buildNeeds(d: PartnersPage): Need[] {
  const needs: Need[] = []
  const used = d.places.used
  for (const a of d.applications) {
    const late = d.now > a.createdAt + DAY
    const f = first(a.name)
    needs.push({
      id: `app:${a.id}`, kind: 'Application', title: `${a.name} wants to be a partner`, detail: hostOf(a.channel),
      due: { text: `${late ? 'Reply was due' : 'Reply by'} ${stamp(a.createdAt + DAY)}`, tone: late ? 3 : 2 },
      buttons: [{ label: 'Accept', what: 'accept', primary: true }, { label: 'Decline', what: 'decline' }],
      ask: {
        accept: `Accept ${f}? Roundly makes their link and sends the welcome email at once. ${d.places.rateNow === 40 ? `They take founding place ${used + 1}, at 40% then 10%.` : 'Founding places are full, so they get 30% then 10%.'}`,
        decline: `Decline ${f}? They get a polite email. This can't be undone.`,
      },
      yes: { accept: `Accept ${f}`, decline: `Decline ${f}` },
      body: { accept: { action: 'accept', id: a.id }, decline: { action: 'decline', id: a.id } },
      said: a,
    })
  }
  for (const p of d.partners) {
    if (!p.payable) continue
    const f = first(p.name)
    const pay = money(p.payable.pence)
    const refunds = p.payable.deductions.length ? `, after ${money(p.deductPence)} refunded on payments already paid` : ''
    const due = { text: `Pay by ${dayShort(isoSec(d.payoutDay))}`, tone: 1 as const }
    if (p.bank) {
      needs.push({
        id: `paid:${p.id}`, kind: 'Payout', title: `${p.name} · ${pay}`,
        detail: (p.stoppedAt ? `final payout: earned before their link stopped on ${dateFull(p.stoppedAt)}, paid even under £20 once 30 days old` : 'bank details received') + refunds,
        due, buttons: [{ label: 'Paid', what: 'paid', primary: true }],
        ask: { paid: `Record that you've sent ${f} ${pay}? Send it from your bank first. Exactly ${pay} is recorded; anything that becomes ready meanwhile stays owing for the next payout.` },
        yes: { paid: `Yes, I've sent ${pay}` },
        body: { paid: { action: 'paid', partnerId: p.id, pence: p.payable.pence, refs: p.payable.refs, deductions: p.payable.deductions } },
      })
    } else {
      needs.push({
        id: `bank:${p.id}`, kind: 'Payout', title: `${p.name} · ${pay}`,
        detail: `no bank details yet: ask for them, then tick Bank details received on ${f}${refunds}`, due,
        buttons: [{ label: `Email ${f}`, what: 'email', primary: true, href: `mailto:${encodeURIComponent(p.email)}?subject=${encodeURIComponent('Your Roundly payout')}` }, { label: `Open ${f}`, what: 'open', open: p.id }],
        ask: {}, yes: {}, body: {},
      })
    }
  }
  for (const c of d.checks) {
    const f = first(c.partnerName)
    const round = d.refs[c.businessRef]
    needs.push({
      id: `check:${c.businessRef}`, kind: 'Referral check', title: `A new business signed up through ${f}${round ? `: ${round.name}` : ''}`,
      detail: `your terms don't pay on a partner's own business or their household's; it pays nothing until you say it counts${c.payments ? `. It has paid ${plural(c.payments, 'time')} already` : ''}`,
      buttons: [{ label: 'It counts', what: 'counts', primary: true }, { label: "Doesn't count", what: 'not' }],
      ask: { counts: `It counts: ${f} earns on it from its first payment.`, not: `Doesn't count: ${f} earns nothing on this business, and gets an email saying why. This can't be undone.` },
      yes: { counts: 'It counts', not: "It doesn't count" },
      body: { counts: { action: 'check', businessRef: c.businessRef, counts: 'yes' }, not: { action: 'check', businessRef: c.businessRef, counts: 'no' } },
    })
  }
  for (const p of d.partners) {
    if (!p.hoodieDue) continue
    const f = first(p.name)
    needs.push({
      id: `hoodie:${p.id}`, kind: 'Hoodie', title: `${p.name} has earned a Roundly hoodie`,
      detail: 'a year as a partner with a paying business: ask lemon or slate, and their size',
      buttons: [{ label: `Email ${f}`, what: 'email', primary: true, href: `mailto:${encodeURIComponent(p.email)}?subject=${encodeURIComponent('Your Roundly hoodie')}` }, { label: 'Sent it', what: 'hoodie' }],
      ask: { hoodie: `Mark ${f}'s hoodie as sent? It moves to Done.` }, yes: { hoodie: 'Yes, sent' },
      body: { hoodie: { action: 'hoodie', partnerId: p.id } },
    })
  }
  for (const q of d.quiet) {
    const p = d.partners.find((x) => x.id === q.partnerId)
    const f = first(q.name)
    const place = p && founding(p) ? p.place : null
    needs.push({
      id: `quiet:${q.partnerId}`, kind: 'Check', title: `${q.name}: no sign-ups in ${monthsWords(d.now - q.joinedAt)}`,
      detail: `if it was a test, delete it${place ? ` to free founding place ${place}` : ''}`,
      buttons: [{ label: `Delete ${f}`, what: 'delete' }, { label: 'Keep', what: 'keep' }],
      ask: {
        delete: `Delete ${q.name}? Only for a test or spam partner: their link stops working${place ? ` and founding place ${place} is free again` : ''}. This can't be undone.`,
        keep: `Keep ${f}? This check comes back in 2 months if there are still no sign-ups.`,
      },
      yes: { delete: `Delete ${f}`, keep: `Keep ${f}` },
      body: { delete: { action: 'delete', partnerId: q.partnerId }, keep: { action: 'keep', partnerId: q.partnerId } },
    })
  }
  return needs
}

function renderNeed(root: HTMLElement, n: Need): HTMLElement {
  const item = el('div', { class: 'rounds-need' })
  const acts = el('div', { class: 'rounds-need-acts' })
  for (const b of n.buttons) {
    if (b.href) {
      acts.append(el('a', { class: `${b.primary ? 'btn-primary' : 'btn-secondary'} rounds-link-button`, href: b.href }, b.label))
      continue
    }
    const button = el('button', { type: 'button', class: b.primary ? 'btn-primary' : 'btn-secondary', 'data-need': n.id, 'data-what': b.what }, b.label)
    if (busy) button.setAttribute('disabled', '')
    button.addEventListener('click', () => {
      if (b.open) return openPartner(root, b.open)
      asking = { id: n.id, what: b.what }
      opener = { id: n.id, what: b.what }
      note = null
      render(root)
    })
    acts.append(button)
  }
  item.append(el('div', { class: 'rounds-need-line' },
    el('span', { class: 'rounds-need-kind' }, n.kind),
    el('span', { class: 'rounds-need-text' }, el('strong', {}, n.title), n.detail ? el('span', { class: 'muted' }, ` ${n.detail}`) : null),
    n.due ? el('span', { class: `rounds-due${n.due.tone > 1 ? ` rounds-due-${n.due.tone}` : ''}` }, n.due.text) : null,
    acts,
  ))
  if (n.said) {
    const link = safeLink(n.said.channel)
    item.append(el('div', { class: 'rounds-need-detail partners-said' },
      link ? el('a', { href: link, target: '_blank', rel: 'noopener noreferrer', class: 'partners-said-link' }, `${hostOf(link)} ↗`) : el('span', {}, n.said.channel),
      el('p', { class: 'partners-audience' }, `“${n.said.audience}”`),
      el('p', { class: 'muted' }, `${n.said.email} · applied ${stamp(n.said.createdAt)}`),
    ))
  }
  if (asking?.id === n.id) item.append(confirmBox(root, n.ask[asking.what] ?? '', n.yes[asking.what] ?? 'Yes', n.id, n.body[asking.what] ?? {}, () => cancelAsk(root)))
  return item
}

// Not now or Escape: the question goes and the focus goes back to the button it came from.
function cancelAsk(root: HTMLElement): void {
  asking = null
  render(root)
  if (opener) root.querySelector<HTMLElement>(`button[data-need="${CSS.escape(opener.id)}"][data-what="${CSS.escape(opener.what)}"]`)?.focus()
}

// Ask first, every time (an email or money can't be taken back). The safe answer has the focus, so Enter never sends.
function confirmBox(root: HTMLElement, ask: string, yes: string, id: string, body: Record<string, unknown>, cancel: () => void, no = 'Not now'): HTMLElement {
  const box = el('div', { class: 'rounds-confirm partners-confirm', role: 'alertdialog', 'aria-label': 'Confirm', 'aria-describedby': `ask-${id.replace(/[^a-z0-9-]/gi, '-')}` })
  const sure = el('button', { type: 'button', class: 'btn-primary' }, busy === id ? 'Working…' : yes)
  const not = el('button', { type: 'button', class: 'btn-secondary' }, no)
  if (busy) { sure.setAttribute('disabled', ''); not.setAttribute('disabled', '') }
  sure.addEventListener('click', () => void act(root, id, body))
  not.addEventListener('click', cancel)
  box.append(el('p', { id: `ask-${id.replace(/[^a-z0-9-]/gi, '-')}` }, ask), el('div', { class: 'mb-confirm-acts' }, sure, not))
  if (!busy) queueMicrotask(() => not.focus())
  return box
}

function noteBox(n: NonNullable<typeof note>): HTMLElement {
  const box = el('div', { class: `partners-note${n.warn ? ' partners-note-warn' : ''}`, role: 'status', tabindex: '-1' }, el('span', {}, n.text))
  if (n.signIn) {
    const sign = el('button', { type: 'button', class: 'btn-primary' }, 'Sign in')
    sign.addEventListener('click', () => window.location.reload())
    box.append(sign)
  }
  return box
}

const KIND_WORDS: Record<string, string> = { accept: 'Application', decline: 'Application', paid: 'Payout', bank: 'Bank details', stop: 'Partner', delete: 'Partner', check: 'Referral check', keep: 'Check', hoodie: 'Hoodie' }
function doneWords(d: PartnersPage, r: DoneRow): { title: string; outcome: string; warn: boolean } {
  const name = r.detail.name ?? 'A partner'
  const on = dateFull(r.at)
  const round = r.detail.businessRef ? d.refs[r.detail.businessRef] : undefined
  const partner = d.partners.find((p) => p.id === r.partnerId)
  switch (r.outcome) {
    case 'accepted': case 'accepted-unsent': {
      const unsent = partner ? !partner.welcomeSent && !partner.stoppedAt : false
      return { title: name, outcome: `Accepted ${on}${unsent ? ', welcome email not sent yet' : ''}`, warn: unsent }
    }
    case 'declined': case 'declined-unsent': {
      const unsent = d.declinesUnsent.some((x) => x.id === r.partnerId)
      return { title: name, outcome: `Declined ${on}${unsent ? ', email not sent yet' : ''}`, warn: unsent }
    }
    case 'paid': return { title: `${name} · ${money(r.detail.pence ?? 0)}`, outcome: `Paid ${on}`, warn: false }
    case 'bank-on': return { title: name, outcome: `Bank details ticked ${on}`, warn: false }
    case 'bank-off': return { title: name, outcome: `Bank details unticked ${on}`, warn: false }
    case 'stopped': return { title: name, outcome: `Link stopped ${on}`, warn: false }
    case 'stopped-unsent': return { title: name, outcome: `Link stopped ${on}, email not sent`, warn: true }
    case 'deleted': return { title: name, outcome: `Deleted ${on}`, warn: false }
    case 'counts': return { title: `A business through ${first(name)}${round ? `: ${round.name}` : ''}`, outcome: `Counts, ${on}`, warn: false }
    case 'not-counted': return { title: `A business through ${first(name)}${round ? `: ${round.name}` : ''}`, outcome: `Doesn't count, ${on}`, warn: false }
    case 'not-counted-unsent': return { title: `A business through ${first(name)}${round ? `: ${round.name}` : ''}`, outcome: `Doesn't count, ${on}, email not sent`, warn: true }
    case 'kept': return { title: name, outcome: `Kept ${on}`, warn: false }
    case 'hoodie-sent': return { title: name, outcome: `Hoodie sent ${on}`, warn: false }
    default: return { title: name, outcome: on, warn: false }
  }
}

function needsSection(root: HTMLElement, d: PartnersPage | null): HTMLElement {
  const section = el('section', { class: 'rounds-needs', 'aria-labelledby': 'partners-needs-h' })
  const needs = d ? buildNeeds(d) : []
  const tabs = el('div', { class: 'rounds-tabs', role: 'tablist', 'aria-label': 'Needs you' })
  for (const [t, label] of [['todo', 'To do'], ['done', 'Done']] as const) {
    const on = tab === t
    const b = el('button', { type: 'button', role: 'tab', id: `partners-tab-${t}`, class: 'rounds-tab', 'aria-selected': String(on), 'aria-controls': 'partners-needs-panel', tabindex: on ? '0' : '-1' }, label, t === 'todo' && d ? el('b', {}, ` ${needs.length}`) : null)
    const pick = (next: 'todo' | 'done'): void => {
      tab = next
      asking = null
      render(root)
      root.querySelector<HTMLElement>('.partners-page .rounds-tab[aria-selected="true"]')?.focus()
    }
    b.addEventListener('click', () => pick(t))
    b.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
      e.preventDefault()
      pick(tab === 'todo' ? 'done' : 'todo')
    })
    tabs.append(b)
  }
  section.append(el('div', { class: 'rounds-needs-head' },
    el('div', {}, el('h3', { id: 'partners-needs-h' }, 'Needs you'), el('p', { class: 'muted' }, 'Applications first (a reply within 24 hours), then payouts, then checks. Each one asks before it is done; email and money cannot be undone.')),
    tabs))
  const body = el('div', { class: 'rounds-needs-body', role: 'tabpanel', id: 'partners-needs-panel', 'aria-labelledby': `partners-tab-${tab}` })
  if (note && !note.panel) body.append(noteBox(note))
  if (!d) {
    body.append(el('p', { class: 'rounds-nothing' }, 'Shown once the partner records answer.'))
  } else if (tab === 'todo') {
    const list = el('div', { class: 'rounds-need-list' })
    for (const n of needs) list.append(renderNeed(root, n))
    body.append(list)
    if (!needs.length) body.append(el('p', { class: 'rounds-nothing' }, d.partners.length ? 'Nothing waiting for you.' : 'Nothing waiting for you. An application shows here the moment it arrives, before anyone is a partner; payouts and checks follow.'))
  } else {
    body.append(el('p', { class: 'muted rounds-done-foot' }, 'Kept for good, newest first, with the date and what was sent or recorded.'))
    const list = el('div', { class: 'rounds-need-list' })
    for (const r of d.done) {
      const w = doneWords(d, r)
      list.append(el('div', { class: 'rounds-need' }, el('div', { class: 'rounds-need-line' },
        el('span', { class: 'rounds-need-kind' }, KIND_WORDS[r.kind] ?? 'Partner'),
        el('span', { class: 'rounds-need-text' }, el('strong', {}, w.title)),
        el('span', { class: w.warn ? 'rounds-due rounds-due-2' : 'rounds-outcome' }, w.outcome),
      )))
    }
    body.append(list)
    if (!d.done.length) body.append(el('p', { class: 'rounds-nothing' }, 'Nothing done here yet.'))
  }
  section.append(body)
  return section
}

// ---- the top -----------------------------------------------------------------------------------------------------

function headLine(d: PartnersPage): string {
  const ps = d.partners
  const stopped = ps.filter((p) => p.stoppedAt).length
  const apps = d.applications.length
  const waiting = apps ? ` ${plural(apps, 'application')} waiting.` : ''
  if (!ps.length) {
    const left = FOUNDING - d.places.used
    return `No partners yet. ${left >= FOUNDING ? `All ${FOUNDING}` : `${left} of ${FOUNDING}`} founding places are open at 40%.${waiting}`
  }
  const brought = ps.reduce((n, p) => n + p.businesses.total, 0)
  const paying = ps.reduce((n, p) => n + p.businesses.paying, 0)
  return `${plural(ps.length, 'partner')}${stopped ? `, ${stopped} stopped` : ''}. ${brought ? `They brought ${plural(brought, 'business', 'businesses')}; ${paying} ${paying === 1 ? 'is' : 'are'} paying.` : 'No businesses through their links yet.'}${waiting}`
}

// Billing reports sign-ups and payments to the partner records; when they aren't getting through, money here is behind.
function billingLine(d: PartnersPage): HTMLElement | null {
  const b = d.billing
  if (!b.on) return el('p', { class: 'rounds-kpis-note' }, 'Billing is off, so no business pays yet. Money shows here once it is on.')
  let text = ''
  if (!b.connected) text = "Billing isn't connected to the partner records, so sign-ups and payments aren't reaching this page."
  else if (b.faults) text = `${plural(b.faults, 'report')} from billing to the partner records ${b.faults === 1 ? 'is' : 'are'} stuck and need looking at, so money here may be missing payments.`
  else if (b.waiting && b.oldestWaiting && d.now - sqlSec(b.oldestWaiting) > 3600) text = `${plural(b.waiting, 'report')} from billing ${b.waiting === 1 ? "hasn't" : "haven't"} reached the partner records since ${stamp(sqlSec(b.oldestWaiting))}, so money here may be behind.`
  return text ? el('div', { class: 'partners-banner', role: 'status' }, text) : null
}

// Accepted or declined, but the email hasn't gone: said until it goes, with a way to write yourself.
function unsentLines(d: PartnersPage): HTMLElement[] {
  const lines: HTMLElement[] = []
  for (const p of d.partners.filter((x) => !x.welcomeSent && !x.stoppedAt && x.joinedAt > d.now - 14 * DAY)) {
    lines.push(el('div', { class: 'partners-banner', role: 'status' },
      el('span', {}, `Accepted ${first(p.name)} at ${p.rate}%; their link is roundly.work/r/${p.code}. ${p.welcomeStillTrying === false ? "The welcome email didn't go, and Roundly has stopped trying: email them yourself." : "The welcome email hasn't gone yet; Roundly keeps trying for two days, and this says so until it goes."}`),
      el('a', { class: 'btn-secondary rounds-link-button', href: `mailto:${encodeURIComponent(p.email)}?subject=${encodeURIComponent('Welcome to Roundly partners')}` }, `Email ${first(p.name)} yourself`)))
  }
  for (const a of d.declinesUnsent) {
    lines.push(el('div', { class: 'partners-banner', role: 'status' },
      el('span', {}, `Declined ${first(a.name)}. The polite email hasn't gone ${a.stillTrying ? 'yet; Roundly keeps trying for two days' : 'and Roundly has stopped trying'}.`),
      el('a', { class: 'btn-secondary rounds-link-button', href: `mailto:${encodeURIComponent(a.email)}?subject=${encodeURIComponent('Your Roundly partner application')}` }, `Email ${first(a.name)} yourself`)))
  }
  return lines
}

function seats(d: PartnersPage, compact: boolean): HTMLElement {
  const holders = d.partners.filter(founding)
  const grid = el('div', { class: `partners-seats${compact ? ' partners-seats-row' : ''}`, role: 'img', 'aria-label': `${holders.length} of ${FOUNDING} founding places used` })
  for (let i = 1; i <= FOUNDING; i++) {
    const p = holders.find((x) => x.place === i)
    grid.append(el('span', { class: `partners-seat${p ? ' partners-seat-on' : ''}`, title: p ? p.name : `Place ${i} free` }, p ? initials(p.name) : String(i)))
  }
  return grid
}

function summary(d: PartnersPage): HTMLElement {
  const ps = d.partners
  const tile = (label: string, value: string | HTMLElement, noteText: string, key = false): HTMLElement =>
    el('div', { class: `rounds-kpi${key ? ' rounds-kpi-key' : ''}` }, el('span', { class: 'rounds-kpi-label' }, label), typeof value === 'string' ? el('strong', { class: 'rounds-kpi-value' }, value) : value, el('span', { class: 'rounds-kpi-note' }, noteText))
  const total = ps.reduce((n, p) => n + p.businesses.total, 0)
  const paying = ps.reduce((n, p) => n + p.businesses.paying, 0)
  const free = ps.reduce((n, p) => n + p.businesses.free, 0)
  const stopped = ps.reduce((n, p) => n + p.businesses.stopped, 0)
  const m = d.month
  const toPay = ps.filter((p) => p.payable && p.bank)
  const noBank = ps.filter((p) => p.payable && !p.bank)
  const rolls = ps.filter((p) => !p.payable && !p.stoppedAt && p.readyPence > 0)
  const payNote = [
    toPay.length ? `by bank transfer to ${toPay.map((p) => first(p.name)).join(' and ')}` : 'nothing to send yet',
    ...noBank.map((p) => `${first(p.name)}'s ${money(p.payable!.pence)} waits for bank details`),
    ...rolls.map((p) => `${first(p.name)}'s ${money(p.readyPence)} rolls over until it reaches £20`),
  ].join(' · ')
  const used = ps.filter(founding).length
  const places = el('div', { class: 'partners-places' }, seats(d, true))
  return el('div', { class: 'rounds-summary' }, el('div', { class: 'rounds-kpis', role: 'group', 'aria-label': 'Partners at a glance' },
    tile('Businesses they brought', String(total), `${paying} paying · ${free} on free months · ${stopped} stopped`),
    tile(`Brought in, ${monthName(d.now)} so far`, money(m.revenuePence), `you keep ${money(m.revenuePence - m.commissionPence)} before card fees · partners earn ${money(m.commissionPence)}${m.allStayCount ? ` · ${money(m.allStayPence)} a month if all ${m.allStayCount} stay` : ''}`, true),
    tile(`Payout day, ${payDayLong(d.payoutDay)}`, `${money(d.payouts.sentPence)} of ${money(d.payouts.sentPence + d.payouts.duePence)} sent`, payNote),
    tile('Founding places', places, `${used} of ${FOUNDING} used: 40% then 10%; after the ${FOUNDING}th, 30% then 10%`),
  ))
}

// Before anyone applies: what happens, the sign-up page, and the places.
function firstSteps(d: PartnersPage): HTMLElement {
  const steps = [
    'Someone applies on roundly.work/partners. It shows up in Needs you here, and you get an email.',
    'You accept or decline within 24 hours. Accepted, they get their own link and a welcome email.',
    'Rounds that sign up through their link show here, and on the Rounds page with "Came from" their link.',
    'When those rounds pay, this page says who to pay and how much, on the 5th.',
  ]
  const copy = el('button', { type: 'button', class: 'btn-primary' }, 'Copy link')
  copy.addEventListener('click', () => {
    void copyText(SIGNUP_PAGE, { button: copy, what: 'link' })
  })
  return el('div', { class: 'partners-first' },
    el('section', { class: 'partners-steps', 'aria-labelledby': 'partners-steps-h' },
      el('h3', { id: 'partners-steps-h' }, 'What happens when the first one arrives'),
      el('ol', {}, ...steps.map((s) => el('li', {}, s))),
      el('div', { class: 'partners-steps-link' }, el('span', {}, 'Partner sign-up page: roundly.work/partners'), copy)),
    el('section', { class: 'partners-places-box', 'aria-labelledby': 'partners-places-h' },
      el('h3', { id: 'partners-places-h' }, 'Founding places'),
      seats(d, false),
      el('p', { class: 'muted' }, `The first ${FOUNDING} partners get 40% of each business's first 12 monthly payments, then 10% of the next 12. After the ${FOUNDING}th: 30%, then 10%.`)))
}

// ---- the partners ------------------------------------------------------------------------------------------------

function readyNote(d: PartnersPage, p: Partner): string {
  if (p.payable) return p.bank ? (p.stoppedAt ? 'final payout' : `to pay ${dateDay(isoSec(d.payoutDay))}`) : 'needs bank details'
  if (p.readyPence > 0 && !p.stoppedAt) return 'rolls over until £20'
  if (p.deductPence > 0) return `${money(p.deductPence)} of refunds to come off`
  return ''
}
function bizDetail(p: Partner): string {
  const b = p.businesses
  if (!b.total) return 'no sign-ups yet'
  return [b.paying ? `${b.paying} paying` : '', b.free ? `${b.free} on free months` : '', b.stopped ? `${b.stopped} stopped` : ''].filter(Boolean).join(', ')
}
function ratePill(p: Partner): HTMLElement {
  if (p.stoppedAt) return el('span', { class: 'partners-pill partners-pill-stopped' }, 'Stopped')
  return el('span', { class: 'partners-pill' }, founding(p) ? '★ 40%' : `${p.rate}%`)
}

function table(root: HTMLElement, d: PartnersPage): HTMLElement {
  const t = el('table', { class: 'rounds-table partners-table' })
  t.append(el('thead', {}, el('tr', {}, ...['Partner', 'Rate', 'Businesses', 'Ready to pay', 'Held 30 days', 'Paid so far', 'Joined'].map((h, i) => el('th', { scope: 'col', class: i >= 3 && i <= 5 ? 'rounds-num' : '' }, h)))))
  const body = el('tbody')
  for (const p of d.partners) {
    const name = el('button', { type: 'button', class: 'rounds-name', 'data-partner': p.id, 'aria-expanded': String(open === p.id) }, p.name)
    name.addEventListener('click', () => openPartner(root, p.id))
    const rn = readyNote(d, p)
    body.append(el('tr', { class: open === p.id ? 'rounds-row-on' : '' },
      el('td', { 'data-label': '' }, name, el('div', { class: 'muted' }, `roundly.work/r/${p.code}`)),
      el('td', { 'data-label': 'Rate' }, ratePill(p)),
      el('td', { 'data-label': 'Businesses' }, el('div', {}, p.businesses.total ? plural(p.businesses.total, 'business', 'businesses') : 'No businesses'), el('div', { class: 'muted' }, bizDetail(p))),
      el('td', { 'data-label': 'Ready to pay', class: 'rounds-num' }, el('div', {}, money(p.readyPence)), rn ? el('div', { class: 'muted' }, rn) : null),
      el('td', { 'data-label': 'Held 30 days', class: 'rounds-num' }, money(p.heldPence)),
      el('td', { 'data-label': 'Paid so far', class: 'rounds-num' }, money(p.paidPence)),
      el('td', { 'data-label': 'Joined' }, dateFull(p.joinedAt)),
    ))
  }
  t.append(body)
  return el('div', { class: 'rounds-table-box' }, t)
}

const FEED_DOT: Record<string, string> = { joined: 'lemon', signup: 'lemon', payment: 'good', payout: 'good', refund: 'bad', stopped: 'bad' }
function feed(root: HTMLElement, d: PartnersPage): HTMLElement {
  const box = el('section', { class: 'partners-feed', 'aria-labelledby': 'partners-feed-h' },
    el('h3', { id: 'partners-feed-h' }, 'What happened'),
    el('p', { class: 'muted' }, 'The last 60 days. The partner records keep no business names, by design.'))
  for (const f of d.feed.slice(0, feedAll ? undefined : 8)) box.append(el('div', { class: 'partners-feed-row' }, el('span', { class: `partners-dot partners-dot-${FEED_DOT[f.kind] ?? 'lemon'}`, 'aria-hidden': 'true' }), el('span', { class: 'partners-feed-text' }, f.text), el('span', { class: 'muted' }, dateDay(f.at))))
  if (!d.feed.length) box.append(el('p', { class: 'rounds-nothing' }, 'Nothing yet.'))
  if (d.feed.length > 8) {
    const more = el('button', { type: 'button', class: 'btn-secondary partners-more', 'aria-expanded': String(feedAll) }, feedAll ? 'Show fewer' : `Show all ${d.feed.length}`)
    more.addEventListener('click', () => { feedAll = !feedAll; render(root); root.querySelector<HTMLElement>('.partners-more')?.focus() })
    box.append(more)
  }
  return box
}

function openPartner(root: HTMLElement, id: string): void {
  if (open === null) scrollBeforeOpen = window.scrollY
  open = id
  stopAsking = false
  bankError = null
  render(root)
  const panel = root.querySelector<HTMLElement>('.rounds-panel')
  if (window.matchMedia(FULL_SCREEN).matches) window.scrollTo({ top: 0 })
  else panel?.scrollIntoView({ block: 'nearest' })
  panel?.querySelector<HTMLElement>('h3')?.focus()
}
function closePartner(root: HTMLElement): void {
  const was = open
  open = null
  stopAsking = false
  render(root)
  if (window.matchMedia(FULL_SCREEN).matches) window.scrollTo({ top: scrollBeforeOpen })
  if (was) root.querySelector<HTMLElement>(`.rounds-name[data-partner="${was}"]`)?.focus({ preventScroll: true })
}

function coming(d: PartnersPage, p: Partner): string {
  if (p.stoppedAt) return 'Nothing: the link is stopped.'
  const year = p.joinedAt + 365 * DAY
  if (p.hoodieSentAt) return `Their Roundly hoodie was sent ${dateFull(p.hoodieSentAt)}.`
  if (p.firstPaidAt) return year <= d.now ? 'A Roundly hoodie is due now: a year as a partner with a paying business. It is in Needs you.' : `A Roundly hoodie in ${monthYear(year)}, a year as a partner with a paying business. Needs you will ask you then: lemon or slate, and their size.`
  return `A Roundly hoodie once they've been a partner a year (${monthYear(year)}) and a business they referred has paid.`
}
function roundLine(d: PartnersPage, p: Partner): string {
  const r = d.rounds[p.code]
  const f = first(p.name)
  if (r?.givenAt && r.freeUntil) return `Linked to ${r.name} on the Rounds page. Free year given on ${dateFull(sqlSec(r.givenAt))}, until ${isoDate(r.freeUntil)}.`
  if (r) return `Linked to ${r.name} on the Rounds page. Their free year comes once a business they referred has paid and 30 days have passed.`
  return `Not linked. If ${f} runs a round, link their code (${p.code}) to it on the Rounds page; their free year comes once a business they referred has paid and 30 days have passed.`
}

function panel(root: HTMLElement, d: PartnersPage, p: Partner): HTMLElement {
  const f = first(p.name)
  const box = el('aside', { class: 'rounds-panel partners-panel', 'aria-label': p.name })
  const back = el('button', { type: 'button', class: 'btn-secondary rounds-back' }, '‹ Partners')
  back.addEventListener('click', () => closePartner(root))
  const close = el('button', { type: 'button', class: 'btn-secondary rounds-close', 'aria-label': 'Close' }, '×')
  close.addEventListener('click', () => closePartner(root))
  box.append(back, el('div', { class: 'rounds-panel-head' }, el('div', {}, el('h3', { tabindex: '-1' }, p.name), el('p', { class: 'muted' }, `roundly.work/r/${p.code} · joined ${dateFull(p.joinedAt)}`)), close))
  const owed = owedOf(p)
  if (p.stoppedAt) {
    box.append(el('div', { class: 'partners-stopped' }, `Link stopped ${dateFull(p.stoppedAt)}: earnings ended that day. Still listed; ${owed > 0 ? `the ${money(owed)} earned before then is paid ${p.payable ? `on ${dateDay(isoSec(d.payoutDay))}` : 'once each payment is 30 days old'}, even under £20.` : 'nothing is owed.'}`))
  }
  box.append(el('p', {}, founding(p)
    ? `40% of each business's first 12 monthly payments, then 10% of the next 12 (founding place ${p.place}; partners after the first ${FOUNDING} get 30%, then 10%).`
    : `${p.rate}% of each business's first 12 monthly payments, then 10% of the next 12.`))
  const fact = (label: string, value: string): HTMLElement => el('div', { class: 'rounds-fact' }, el('span', { class: 'rounds-fact-label' }, label), el('span', { class: 'rounds-fact-value' }, value))
  box.append(el('div', { class: 'rounds-facts' }, fact('Ready to pay', money(p.readyPence)), fact('Held 30 days for refunds', money(p.heldPence)), fact('Paid so far', money(p.paidPence))))
  const extra = [
    p.checkPence ? `${money(p.checkPence)} more waits for your referral check in Needs you.` : '',
    p.deductPence ? `${money(p.deductPence)} refunded on payments already paid out comes off the next payout.` : '',
  ].filter(Boolean).join(' ')
  if (extra) box.append(el('p', { class: 'muted' }, extra))
  const b = p.businesses
  const quiet = !p.stoppedAt && b.total && p.lastSignupAt && d.now - p.lastSignupAt > 60 * DAY ? ` No sign-ups in the last ${monthsWords(d.now - p.lastSignupAt)}.` : ''
  box.append(el('p', { class: 'muted' }, `${b.total ? `${plural(b.total, 'business', 'businesses')}: ${bizDetail(p)}.` : 'No businesses yet.'}${p.couldPence ? ` ${money(p.couldPence)} a month to ${f} if all stay.` : ''}${quiet}`))
  // Bank details received: a tick, saved at once (the details themselves stay in Liam's email).
  const tick = el('input', { type: 'checkbox', id: `partner-bank-${p.id}` }) as HTMLInputElement
  tick.checked = p.bank
  if (busy) tick.disabled = true
  tick.addEventListener('change', () => {
    const want = tick.checked
    tick.disabled = true
    bankError = null
    const key = crypto.randomUUID()
    void changeNow<ActionAnswer>('POST', '/api/admin/partners/action', { action: 'bank', partnerId: p.id, on: want }, key)
      .then(() => load(root))
      .catch((err: ApiError) => {
        bankError = err.message === SIGNED_OUT_MESSAGE ? "You've been signed out. Sign in again to change this." : `Not saved: ${[400, 404, 409, 422].includes(err.status) ? err.message : "can't reach the partner records right now. Try again."}`
        if (active()) render(root)
      })
  })
  box.append(el('label', { class: 'partners-bank', for: `partner-bank-${p.id}` }, tick, el('span', {}, 'Bank details received (the details stay in your email, never in Roundly)')))
  if (bankError) box.append(el('p', { class: 'form-error', role: 'alert' }, bankError))
  box.append(el('div', { class: 'partners-box' }, el('strong', {}, 'Their own round'), el('span', {}, roundLine(d, p))))
  box.append(el('div', { class: 'partners-box' }, el('strong', {}, 'Coming up'), el('span', {}, coming(d, p))))
  const acts = el('div', { class: 'rounds-need-acts' }, el('a', { class: 'btn-secondary rounds-link-button', href: `mailto:${encodeURIComponent(p.email)}` }, `Email ${f}`))
  if (!p.stoppedAt) {
    const stop = el('button', { type: 'button', class: 'partners-stop' }, `Stop ${f}'s link`)
    if (busy) stop.setAttribute('disabled', '')
    stop.addEventListener('click', () => { stopAsking = true; note = null; render(root) })
    acts.append(stop)
  }
  box.append(acts)
  if (stopAsking && !p.stoppedAt) {
    box.append(confirmBox(root,
      `Stop ${f}'s link? Their earnings end today: payments after today earn nothing, and their partner account closes. ${f} stays listed here and gets an email. What they earned before today (${money(owed)}) is still paid, even under £20, once each payment is 30 days old.`,
      'Stop the link', `stop:${p.id}`, { action: 'stop', partnerId: p.id }, () => { stopAsking = false; render(root); root.querySelector<HTMLElement>('.partners-stop')?.focus() }, 'Keep it'))
  }
  if (note?.panel) box.append(noteBox(note))
  return box
}

// ---- the page ----------------------------------------------------------------------------------------------------

function render(root: HTMLElement): void {
  if (!active()) return
  clear(root)
  const d = data
  const shown = d && open ? d.partners.find((p) => p.id === open) ?? null : null
  const page = el('div', { class: `rounds-page partners-page${shown ? ' rounds-detail-open' : ''}` })
  root.append(page)
  const head = el('div', { class: 'rounds-head' }, el('h2', {}, 'Partners'))
  page.append(head)
  if (!d) {
    if (failed === 'signed-out') {
      const sign = el('button', { type: 'button', class: 'btn-primary' }, 'Sign in')
      sign.addEventListener('click', () => window.location.reload())
      head.append(el('div', { class: 'partners-banner', role: 'alert' }, el('span', {}, "You've been signed out. Sign in again to see the partners."), sign))
    } else if (failed) {
      const again = el('button', { type: 'button', class: 'btn-secondary' }, loading ? 'Trying…' : 'Try again')
      again.addEventListener('click', () => { void load(root); render(root) })
      head.append(el('div', { class: 'partners-banner', role: 'alert' },
        el('span', {}, "Can't reach the partner records right now, so nothing here is up to date. Money figures are hidden rather than shown as £0.00. Accept, Decline and Paid need a connection and are never saved to send later."),
        again))
    } else head.append(el('p', { class: 'muted' }, 'Loading…'))
    page.append(needsSection(root, null))
    return
  }
  head.append(el('p', { class: 'rounds-lead' }, headLine(d)))
  const billing = billingLine(d)
  if (billing) head.append(billing)
  for (const line of unsentLines(d)) head.append(line)
  if (d.partners.length) page.append(summary(d))
  else page.append(el('div', { class: 'rounds-summary' }, firstSteps(d)))
  page.append(needsSection(root, d))
  if (d.partners.length) {
    page.append(el('div', { class: 'rounds-main' },
      el('div', { class: 'rounds-list-col' }, table(root, d), feed(root, d)),
      shown ? panel(root, d, shown) : null))
  }
}

let escapeOn: HTMLElement | null = null
// Escape: a question closes first, then an open partner.
function onKey(root: HTMLElement, e: KeyboardEvent): void {
  if (e.key !== 'Escape' || !active() || busy) return
  if (asking) { cancelAsk(root); return }
  if (stopAsking) { stopAsking = false; render(root); root.querySelector<HTMLElement>('.partners-stop')?.focus(); return }
  if (open) closePartner(root)
}

export function mountPartners(root: HTMLElement, reader: Read, isActive: () => boolean): void {
  read = reader
  active = isActive
  data = null
  failed = null
  tab = 'todo'
  asking = null
  stopAsking = false
  open = null
  note = null
  busy = null
  bankError = null
  feedAll = false
  keys.clear()
  if (escapeOn !== root) {
    root.addEventListener('keydown', (e) => onKey(root, e))
    escapeOn = root
  }
  render(root)
  void load(root)
}
